import { Spinner } from "@/components/ui/spinner";
import FriendAvatar from "@/components/FriendAvatar";
import { useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, Pencil, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShareTone, type SplitGroup, type SplitGroupMember } from "@boo/shared";
import { useSplitGroupStore } from "@/stores/useSplitGroupStore";
import {
  TEMPLATE_PLACEHOLDERS,
  defaultTemplate,
} from "@/features/bills/lib/shareMessage";
import { INPUT_CLASS } from "../types/friends.types";
import FriendPicker from "./FriendPicker";

// ── Member row ────────────────────────────────────────────────────────────────

function MemberItem({
  groupId,
  member,
}: {
  groupId: string;
  member: SplitGroupMember;
}) {
  const { removeMember } = useSplitGroupStore();
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    setDeleting(true);
    try {
      await removeMember(groupId, member.id);
    } catch {
      toast.error("Failed to remove member");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <li className="flex items-center gap-2 py-2">
      <FriendAvatar
        name={member.friend.name}
        avatar={member.friend.avatar}
        friendId={member.friend.id}
        size={28}
      />
      <div className="flex-1 min-w-0">
        <span className="text-[13px] font-medium text-foreground">
          {member.friend.name}
        </span>
        {member.friend.contactInfo && (
          <span className="block text-[12px] text-muted-foreground sm:ml-2 sm:inline">
            {member.friend.contactInfo}
          </span>
        )}
      </div>
      <button
        onClick={handleDelete}
        disabled={deleting}
        className="shrink-0 size-10 sm:size-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-40 transition-colors"
        aria-label={`Remove ${member.friend.name}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </li>
  );
}

// ── Add member ────────────────────────────────────────────────────────────────

function AddMemberRow({ group }: { group: SplitGroup }) {
  const { addMember } = useSplitGroupStore();

  return (
    <div className="mt-2 flex">
      <FriendPicker
        placeholder="Add a member"
        excludeIds={group.members.map((m) => m.friend.id)}
        onPickFriend={async (friend) => {
          await addMember(group.id, { friendId: friend.id });
        }}
      />
    </div>
  );
}

// ── Share message wording ─────────────────────────────────────────────────────

function WordingEditor({ group }: { group: SplitGroup }) {
  const { updateGroup } = useSplitGroupStore();
  const savedOpen =
    group.shareMessageOpen ?? defaultTemplate(group.tone, false);
  const savedFinal =
    group.shareMessageFinal ?? defaultTemplate(group.tone, true);
  const [open, setOpen] = useState(savedOpen);
  const [final, setFinal] = useState(savedFinal);
  const [busy, setBusy] = useState(false);
  const dirty = open !== savedOpen || final !== savedFinal;
  const custom =
    group.shareMessageOpen !== null || group.shareMessageFinal !== null;

  async function save(patch: {
    shareMessageOpen: string | null;
    shareMessageFinal: string | null;
  }) {
    setBusy(true);
    try {
      await updateGroup(group.id, patch);
      toast.success("Wording saved");
    } catch {
      toast.error("Failed to save wording");
    } finally {
      setBusy(false);
    }
  }

  const area = (label: string, value: string, set: (v: string) => void) => (
    <label className="flex flex-col gap-1 text-[12px] text-muted-foreground">
      {label}
      <textarea
        value={value}
        onChange={(e) => set(e.target.value)}
        rows={4}
        maxLength={1000}
        className={`${INPUT_CLASS} w-full resize-y py-2 text-base sm:text-xs`}
      />
    </label>
  );

  return (
    <div className="mb-3 space-y-2">
      <div className="text-[12px] text-muted-foreground">
        Share message wording · use {TEMPLATE_PLACEHOLDERS}
      </div>
      {area("Before finalising", open, setOpen)}
      {area("After finalising", final, setFinal)}
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() =>
            save({ shareMessageOpen: open, shareMessageFinal: final })
          }
          disabled={busy || !dirty}
          className="flex-1 sm:flex-none"
        >
          Save wording
        </Button>
        {custom && (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              save({ shareMessageOpen: null, shareMessageFinal: null })
            }
            disabled={busy}
          >
            Reset to default
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Group card ────────────────────────────────────────────────────────────────

export default function GroupCard({ group }: { group: SplitGroup }) {
  const { updateGroup, deleteGroup } = useSplitGroupStore();
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(group.name);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleSaveName() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await updateGroup(group.id, { name: name.trim() });
      setEditingName(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteGroup(group.id);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          {editingName ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                className={`${INPUT_CLASS} flex-1 min-w-0 font-semibold`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
              />
              <button
                onClick={handleSaveName}
                disabled={saving || !name.trim()}
                className="size-10 sm:size-7 flex items-center justify-center rounded-md text-green-600 hover:bg-green-50 dark:hover:bg-green-950/30 disabled:opacity-40 transition-colors"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => {
                  setName(group.name);
                  setEditingName(false);
                }}
                className="size-10 sm:size-7 flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setEditingName(true)}
              className="flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-foreground/70 transition-colors group"
            >
              {group.name}
              <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          )}

          {!editingName &&
            (confirmDelete ? (
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[12px] text-muted-foreground">
                  Delete group?
                </span>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="inline-flex items-center gap-1 text-[12px] font-medium text-red-500 hover:text-red-600 disabled:opacity-40"
                >
                  {deleting && <Spinner className="size-3" />}
                  {deleting ? "Deleting…" : "Yes"}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="text-[12px] text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                className="shrink-0 size-10 sm:size-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                aria-label={`Delete ${group.name}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            ))}
        </div>

        <CardTitle className="text-[11px] font-normal text-muted-foreground">
          {group.members.length}{" "}
          {group.members.length === 1 ? "member" : "members"}
        </CardTitle>
      </CardHeader>

      <CardContent>
        <label className="mb-3 flex flex-col gap-1 text-[12px] text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:gap-2">
          Share message tone
          <span className="relative w-full sm:w-auto">
            <select
              value={group.tone}
              onChange={(e) =>
                updateGroup(group.id, { tone: e.target.value as ShareTone })
              }
              className={`${INPUT_CLASS} w-full appearance-none py-2 pr-9 text-base sm:w-auto sm:py-1 sm:text-sm`}
            >
              <option value={ShareTone.Neutral}>Neutral</option>
              <option value={ShareTone.Playful}>Playful (close friends)</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          </span>
        </label>
        <WordingEditor
          key={`${group.tone}|${group.shareMessageOpen}|${group.shareMessageFinal}`}
          group={group}
        />
        {group.members.length > 0 && (
          <ul className="divide-y divide-border mb-1">
            {group.members.map((m) => (
              <MemberItem key={m.id} groupId={group.id} member={m} />
            ))}
          </ul>
        )}
        <AddMemberRow group={group} />
      </CardContent>
    </Card>
  );
}
